/**
 * Enrollment Sync API Service
 * Handles enrollment data synchronization with SIS database
 */
import authService from './auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

export interface SyncResult {
  status: string;
  message: string;
  sync_results?: {
    total_records: number;
    imported: number;
    updated: number;
    skipped: number;
    errors: number;
  };
  summary?: {
    academic_year: number;
    total_records: number;
    total_schools: number;
    total_male: number;
    total_female: number;
    total_students: number;
    female_percentage: number;
    gender_parity_index: number | null;
  };
}

export interface SyncStatus {
  status: string;
  academic_year: number;
  summary: {
    academic_year: number;
    total_records: number;
    total_schools: number;
    total_male: number;
    total_female: number;
    total_students: number;
    female_percentage: number;
    gender_parity_index: number | null;
  };
  last_sync: {
    timestamp: string;
    status: string;
    total_records: number;
    successful_records: number;
    failed_records: number;
  } | null;
  config_name: string;
}

export const syncEnrollmentData = async (
  academicYear?: number, 
  clearExisting: boolean = false
): Promise<SyncResult> => {
  const response = await fetch(`${API_BASE_URL}/sis-integration/sync-enrollment/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authService.getAuthHeaders(),
    },
    body: JSON.stringify({
      academic_year: academicYear,
      clear_existing: clearExisting,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

export const triggerFullSync = async (): Promise<SyncResult> => {
  const response = await fetch(`${API_BASE_URL}/sis-integration/sync-full/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authService.getAuthHeaders(),
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

export const getEnrollmentSyncStatus = async (academicYear?: number): Promise<SyncStatus> => {
  const url = new URL(`${API_BASE_URL}/sis-integration/enrollment-sync-status/`);
  if (academicYear) {
    url.searchParams.append('academic_year', academicYear.toString());
  }

  const response = await fetch(url.toString(), {
    headers: authService.getAuthHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

export const getAvailableAcademicYears = async (): Promise<{ academic_years: number[]; current_year: number }> => {
  const response = await fetch(`${API_BASE_URL}/sis-integration/enrollment-available-years/`, {
    headers: authService.getAuthHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};